import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Plus, Edit, Trash2, Tags, Package } from 'lucide-react';
import MobilePageHeader from '../components/MobilePageHeader';

const SECTORS = ["Bar", "Cuisine", "Salle", "Autre"];
const SUB_CATEGORIES = ["Froid", "Produit sec", "Frais", "Surgelé", "Boisson", "Autre"];

const CategoriesPage = () => {
    const { api } = useAuth();
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [filterSector, setFilterSector] = useState('all');
    const [formData, setFormData] = useState({ 
        name: '', 
        description: '',
        sector: 'Cuisine',
        sub_category: 'Autre'
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [categoriesRes, productsRes] = await Promise.all([
                api().get('/categories'),
                api().get('/products')
            ]);
            setCategories(categoriesRes.data);
            setProducts(productsRes.data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const getProductCount = (categoryId) => {
        return products.filter(p => p.category_id === categoryId).length;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCategory) {
                await api().put(`/categories/${editingCategory.id}`, formData);
                toast.success('Catégorie modifiée');
            } else {
                await api().post('/categories', formData);
                toast.success('Catégorie créée');
            }
            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleEdit = (category) => {
        setEditingCategory(category);
        setFormData({ 
            name: category.name, 
            description: category.description,
            sector: category.sector || 'Cuisine',
            sub_category: category.sub_category || 'Autre'
        });
        setDialogOpen(true);
    };

    const handleDelete = async (category) => {
        const count = getProductCount(category.id);
        if (count > 0) {
            toast.error(`Impossible: ${count} produits utilisent cette catégorie`);
            return;
        }
        if (!window.confirm(`Supprimer "${category.name}" ?`)) return;
        
        try {
            await api().delete(`/categories/${category.id}`);
            toast.success('Catégorie supprimée');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const resetForm = () => {
        setEditingCategory(null);
        setFormData({ name: '', description: '', sector: 'Cuisine', sub_category: 'Autre' });
    };

    const filteredCategories = categories.filter(cat => {
        if (filterSector === 'all') return true;
        return cat.sector === filterSector;
    });

    // Group categories by sector
    const groupedCategories = filteredCategories.reduce((acc, cat) => {
        const sector = cat.sector || 'Autre';
        if (!acc[sector]) acc[sector] = [];
        acc[sector].push(cat);
        return acc;
    }, {});

    if (loading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-muted rounded w-48"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="h-32 bg-muted rounded-xl"></div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="categories-page">
            {/* Mobile Page Header */}
            <MobilePageHeader 
                title="Catégories"
                subtitle={`${categories.length} catégories`}
                icon={Tags}
                backPath="/dashboard"
            />
            
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold text-foreground tracking-tight">
                        Catégories
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Organisez vos produits par secteur et catégorie
                    </p>
                </div>
                <div className="flex gap-2">
                    <Select value={filterSector} onValueChange={setFilterSector}>
                        <SelectTrigger className="w-40">
                            <SelectValue placeholder="Tous secteurs" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous secteurs</SelectItem>
                            {SECTORS.map((sector) => (
                                <SelectItem key={sector} value={sector}>
                                    {sector}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                        <DialogTrigger asChild>
                            <Button className="bg-accent hover:bg-accent/90 btn-press" data-testid="add-category-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Nouvelle catégorie
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="font-display">
                                    {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
                                </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Nom</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        placeholder="Ex: Légumes frais"
                                        required
                                        data-testid="category-name-input"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="sector">Secteur</Label>
                                        <Select 
                                            value={formData.sector} 
                                            onValueChange={(value) => setFormData({...formData, sector: value})}
                                        >
                                            <SelectTrigger data-testid="category-sector-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {SECTORS.map((sector) => (
                                                    <SelectItem key={sector} value={sector}>
                                                        {sector}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="sub_category">Sous-catégorie</Label>
                                        <Select 
                                            value={formData.sub_category} 
                                            onValueChange={(value) => setFormData({...formData, sub_category: value})}
                                        >
                                            <SelectTrigger data-testid="category-subcategory-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {SUB_CATEGORIES.map((sub) => (
                                                    <SelectItem key={sub} value={sub}>
                                                        {sub}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="description">Description (optionnel)</Label>
                                    <Textarea
                                        id="description"
                                        value={formData.description}
                                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                                        placeholder="Description de la catégorie..."
                                        rows={3}
                                        data-testid="category-description-input"
                                    />
                                </div>
                                <div className="flex justify-end gap-3 pt-4">
                                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                        Annuler
                                    </Button>
                                    <Button type="submit" className="bg-accent hover:bg-accent/90" data-testid="category-submit-btn">
                                        {editingCategory ? 'Modifier' : 'Créer'}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Categories by Sector */}
            {Object.keys(groupedCategories).length === 0 ? (
                <Card className="p-12">
                    <div className="text-center">
                        <Tags className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                        <p className="text-muted-foreground mb-4">Aucune catégorie créée</p>
                        <Button 
                            onClick={() => setDialogOpen(true)}
                            className="bg-accent hover:bg-accent/90"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Créer une catégorie
                        </Button>
                    </div>
                </Card>
            ) : (
                <div className="space-y-8">
                    {Object.entries(groupedCategories).map(([sector, sectorCategories]) => (
                        <div key={sector}>
                            <h2 className="font-display text-xl font-semibold mb-4 flex items-center gap-2">
                                <Badge variant="outline" className="bg-accent/10 text-accent border-accent/30">
                                    {sector}
                                </Badge>
                                <span className="text-muted-foreground text-sm font-normal">
                                    ({sectorCategories.length} catégorie{sectorCategories.length > 1 ? 's' : ''})
                                </span>
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {sectorCategories.map((category, index) => (
                                    <Card 
                                        key={category.id} 
                                        className="card-hover animate-fadeIn"
                                        style={{ animationDelay: `${index * 0.05}s` }}
                                        data-testid={`category-card-${category.id}`}
                                    >
                                        <CardContent className="p-6">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                                                            <Tags className="w-5 h-5 text-accent" />
                                                        </div>
                                                        <div>
                                                            <h3 className="font-display font-semibold text-lg text-foreground">
                                                                {category.name}
                                                            </h3>
                                                            <Badge variant="outline" className="text-xs">
                                                                {category.sub_category || 'Autre'}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                    {category.description && (
                                                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                                                            {category.description}
                                                        </p>
                                                    )}
                                                    <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
                                                        <Package className="w-4 h-4" />
                                                        <span>{getProductCount(category.id)} produit(s)</span>
                                                    </div>
                                                </div>
                                                <div className="flex gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleEdit(category)}
                                                        data-testid={`edit-category-${category.id}`}
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleDelete(category)}
                                                        className="text-destructive hover:text-destructive"
                                                        data-testid={`delete-category-${category.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CategoriesPage;
